<template>
  <div
    id="inputContainer"
    ref="datepickerRef"
    class="relative max-w-[880px]"
    @click="openDropdown"
  >
    <label
      v-if="label"
      for="input-group-1"
      class="block mb-2 text-md font-medium text-gray-900 dark:text-white pl-1"
    >
      {{ label }}
    </label>

    <div class="relative">
      <label
        for="input-group-1"
        class="absolute transition-all duration-200 ease-in-out pointer-events-none"
        :class="{
          'text-xs top-[calc(0.25rem+3px)] left-4 text-blue-600':
            isFocused || dateInput,
          'text-gray-500 dark:text-gray-300 text-md top-1/2 left-4 transform -translate-y-1/2':
            !(isFocused || dateInput)
        }"
        style="left: 1rem"
      >
        {{ placeholder }}
      </label>

      <input
        id="input-group-1"
        v-model="dateInput"
        type="text"
        class="bg-white border border-gray-300 text-md text-gray-900 rounded-md focus:ring-blue-600 focus:border-blue-600 block w-full pl-4 pt-6 pb-3 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500"
        :style="{
          height: '42px'
        }"
        maxlength="10"
        @input="handleInputChange"
        @focus="handleFocus"
        @blur="handleBlur"
        @keydown="handleKeydown"
      />

      <div
        class="absolute inset-y-0 end-0 flex items-center pe-3.5 pointer-events-none"
      >
        <svg
          class="w-4 h-4 text-gray-500 dark:text-gray-400"
          xmlns="http://www.w3.org/2000/svg"
          fill="currentColor"
          viewBox="0 0 27.875 27.875"
          @click="toggleDropdown"
        >
          <path
            d="M25.97,2.893h-5.441V1.5c0-0.829-0.672-1.5-1.5-1.5c-0.827,0-1.5,0.671-1.5,1.5v1.393h-7.182V1.5 c0-0.829-0.672-1.5-1.5-1.5c-0.828,0-1.5,0.671-1.5,1.5v1.393H1.904c-0.828,0-1.5,0.671-1.5,1.5v21.982c0,0.83,0.672,1.5,1.5,1.5 h24.066c0.828,0,1.5-0.67,1.5-1.5V4.393C27.47,3.564,26.797,2.893,25.97,2.893z M3.403,24.876V9.619H24.47v15.256L3.403,24.876 L3.403,24.876z"
          />
        </svg>
      </div>
    </div>

    <div
      v-if="isDropdownOpen"
      id="dateDropdown"
      class="absolute left-0 top-full mt-2 bg-white rounded-md shadow-lg border border-gray-300 z-10 p-0 m-0 pt-1"
      @click.stop
    >
      <div class="flex">
        <div class="w-28 max-h-56 overflow-y-auto custom-scrollbar">
          <p
            class="px-4 py-3 font-semibold text-gray-900 sticky top-0 bg-white"
          >
            Month
          </p>
          <ul class="py-0.5 text-sm text-gray-700">
            <li
              v-for="(month, index) in months"
              :key="index"
              :class="{
                'bg-blue-100 border-l-2 border-blue-600':
                  selectedMonth === index + 1,
                'hover:bg-gray-100': selectedMonth !== index + 1
              }"
              class="px-4 py-1 cursor-pointer"
              @click.stop="selectMonth(index + 1)"
            >
              {{ month }}
            </li>
          </ul>
        </div>
        <div class="w-px bg-gray-300"></div>
        <div class="w-28 max-h-56 overflow-y-auto custom-scrollbar">
          <p
            class="px-4 py-3 font-semibold text-gray-900 sticky top-0 bg-white"
          >
            Day
          </p>
          <ul class="py-0.5 text-sm text-gray-700">
            <li
              v-for="day in days"
              :key="day"
              :class="{
                'bg-blue-100 border-l-2 border-blue-600': selectedDay === day,
                'hover:bg-gray-100': selectedDay !== day
              }"
              class="px-4 py-1 cursor-pointer"
              @click.stop="selectDay(day)"
            >
              {{ day < 10 ? "0" + day : day }}
            </li>
          </ul>
        </div>
        <div class="w-px bg-gray-300"></div>
        <div class="w-28 max-h-56 overflow-y-auto custom-scrollbar">
          <p
            class="px-4 py-3 font-semibold text-gray-900 sticky top-0 bg-white"
          >
            Year
          </p>
          <ul class="py-0.5 text-sm text-gray-700">
            <li
              v-for="year in years"
              :key="year"
              :class="{
                'bg-blue-100 border-l-2 border-blue-600': selectedYear === year,
                'hover:bg-gray-100': selectedYear !== year
              }"
              class="px-4 py-1 cursor-pointer"
              @click.stop="selectYear(year)"
            >
              {{ year }}
            </li>
          </ul>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import moment from "moment-timezone";
import { onBeforeUnmount, onMounted, ref, watch } from "vue";

const months = [
  "1 January",
  "2 February",
  "3 March",
  "4 April",
  "5 May",
  "6 June",
  "7 July",
  "8 August",
  "9 September",
  "10 October",
  "11 November",
  "12 December"
];

const days = Array.from({ length: 31 }, (_, i) => i + 1);
// Recent years, current on top, a generous window back for correcting/backdating
// past appointments plus one year forward.
const currentYear = new Date().getFullYear();
const years = Array.from({ length: 8 }, (_, i) => currentYear + 1 - i);

const emit = defineEmits(["update:modelValue"]);
const props = withDefaults(
  defineProps<{ label?: string; modelValue: string; placeholder?: string }>(),
  { label: "", placeholder: "mm/dd/yy" }
);
const datepickerRef = ref<HTMLElement | null>(null);
const selectedMonth = ref<number | null>(null);
const selectedDay = ref<number | null>(null);
const selectedYear = ref<number | null>(null);
const isDropdownOpen = ref(false);
const dateInput = ref(props.modelValue);
const isFocused = ref(false);

const twoDigitYear = (year: number) => String(year).slice(-2).padStart(2, "0");

// Normalize a 2- or 4-digit year to a full year (moment's "YY" pivot: 00-68 =>
// 2000s, 69-99 => 1900s — fine for appointment dates).
const fullYear = (raw: string): number => {
  const n = parseInt(raw, 10);
  return raw.length <= 2 ? 2000 + n : n;
};

// Rebuild the MM/DD/YY input string from the selected parts.
const syncDateInput = () => {
  const month = selectedMonth.value
    ? String(selectedMonth.value).padStart(2, "0")
    : "";
  const day = selectedDay.value
    ? String(selectedDay.value).padStart(2, "0")
    : "";
  let out = month;
  if (day) out += `/${day}`;
  if (month && day && selectedYear.value != null) {
    out += `/${twoDigitYear(selectedYear.value)}`;
  }
  dateInput.value = out;
};

onMounted(() => {
  document.addEventListener("click", handleClickOutside);
  if (!props.modelValue) return;

  // Prefer the literal MM/DD[/YY] already in the value — running it through
  // moment(val) with a short string falls back to new Date() (UTC midnight),
  // which shifts the day in non-UTC tenants.
  const parts = props.modelValue.match(/^(\d{1,2})\/(\d{1,2})(?:\/(\d{2,4}))?/);
  if (parts) {
    selectedMonth.value = parseInt(parts[1], 10);
    selectedDay.value = parseInt(parts[2], 10);
    // Preserve the appointment's actual year; default to the current year only
    // when the value carried none.
    selectedYear.value = parts[3] ? fullYear(parts[3]) : currentYear;
  } else {
    const parsed = moment(props.modelValue);
    if (parsed.isValid()) {
      selectedMonth.value = parsed.month() + 1;
      selectedDay.value = parsed.date();
      selectedYear.value = parsed.year();
    }
  }
  syncDateInput();
});

onBeforeUnmount(() => {
  document.removeEventListener("click", handleClickOutside);
});

function toggleDropdown() {
  isDropdownOpen.value = !isDropdownOpen.value;
}

function handleInputChange(event: Event) {
  const raw = (event.target as HTMLInputElement).value;
  isDropdownOpen.value = true;

  // Keep only digits, then re-insert the slashes at the MM/DD/YYYY boundaries.
  // Accepting a full 4-digit year lets a custom year outside the dropdown (e.g.
  // 2019) be typed without maxlength truncating it to the current decade;
  // 2-digit years ("06/22/19") still parse. The dropdown normalizes to 2-digit.
  const digits = raw.replace(/\D/g, "").slice(0, 8);
  let formatted = digits.slice(0, 2);
  if (digits.length > 2) formatted += `/${digits.slice(2, 4)}`;
  if (digits.length > 4) formatted += `/${digits.slice(4, 8)}`;
  dateInput.value = formatted;

  const [monthStr, dayStr, yearStr] = formatted.split("/");
  selectedMonth.value = monthStr ? parseInt(monthStr, 10) : null;
  selectedDay.value = dayStr ? parseInt(dayStr, 10) : null;
  if (yearStr) selectedYear.value = fullYear(yearStr);
}

function handleKeydown(event: KeyboardEvent) {
  if (
    !/^\d$/.test(event.key) &&
    event.key !== "/" &&
    event.key !== "Backspace"
  ) {
    event.preventDefault();
  }
}

function handleFocus() {
  isFocused.value = true;
}

function handleBlur() {
  isFocused.value = false;
}

function selectMonth(month: number) {
  selectedMonth.value = month;
  syncDateInput();
}

function selectDay(day: number) {
  if (selectedMonth.value === null) {
    selectedMonth.value = new Date().getMonth() + 1;
  }
  if (selectedYear.value === null) {
    selectedYear.value = currentYear;
  }
  selectedDay.value = day;
  syncDateInput();
}

function selectYear(year: number) {
  selectedYear.value = year;
  syncDateInput();
}

function handleClickOutside(event: MouseEvent) {
  if (
    datepickerRef.value &&
    !datepickerRef.value.contains(event.target as Node)
  ) {
    isDropdownOpen.value = false;
  }
}
const openDropdown = () => {
  isDropdownOpen.value = true;
};

watch(dateInput, (newVal, oldVal) => {
  if (newVal !== oldVal) {
    emit("update:modelValue", newVal);
  }
});
</script>
<style scoped>
.custom-scrollbar {
  scrollbar-width: thin;
  scrollbar-color: #d1d5db transparent;
}

.custom-scrollbar::-webkit-scrollbar {
  width: 8px;
}

.custom-scrollbar::-webkit-scrollbar-track {
  background: transparent;
}

.custom-scrollbar::-webkit-scrollbar-thumb {
  background-color: #e5e7eb;
  border-radius: 10px;
  width: 8px;
  min-height: 20px;
  height: 20px;
  margin: 0;
}

.custom-scrollbar ul {
  white-space: nowrap;
  margin: 0;
  padding: 0;
}

.custom-scrollbar li {
  display: flex;
  align-items: center;
  padding: 0.25rem 1rem;
  text-overflow: ellipsis;
  overflow: hidden;
  white-space: nowrap;
}

.w-28 {
  min-width: 140px;
}

.flex {
  display: flex;
  align-items: stretch;
}

.custom-scrollbar li {
  height: 2rem;
}

#dateDropdown {
  padding-bottom: 0;
  margin: 0;
  padding-top: 0.25rem;
}

.custom-scrollbar p {
  position: sticky;
  top: 0;
  background-color: white;
  z-index: 1;
  padding: 0.5rem 1rem;
  font-weight: 600;
  color: #4b5563;
}
</style>
